import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Mail, RefreshCw, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import mfaService from '../../services/mfaService';
import MFARecoveryLink from './MFARecoveryLink';

const MFAVerification = ({ onVerificationComplete, onCancel, userEmail, method = 'totp' }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerification = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      const result = await mfaService.verifyMFA(verificationCode, method);
      toast.success('Verification successful!');
      onVerificationComplete(result);
    } catch (error) {
      console.error('Error verifying MFA:', error);
      if (error.response?.status === 400) {
        toast.error('Invalid verification code. Please try again.');
      } else {
        toast.error('Verification failed. Please try again.');
      }
      setVerificationCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (method !== 'email' || resendCooldown > 0) return;

    try {
      setLoading(true);
      // This would trigger a new email code to be sent
      await mfaService.setupMFA('email');
      toast.success('New verification code sent to your email');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      console.error('Error resending code:', error);
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerification();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Shield className="w-6 h-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Verify Your Identity
              </h2>
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>

          <div className="space-y-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {method === 'totp' ? (
                  <Smartphone className="w-12 h-12 text-primary-600" />
                ) : (
                  <Mail className="w-12 h-12 text-blue-600" />
                )}
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {method === 'totp' ? 'Enter Authenticator Code' : 'Check Your Email'}
              </h3>
              
              {method === 'totp' ? (
                <p className="text-sm text-gray-600">
                  Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    We've sent a 6-digit verification code to:
                  </p>
                  <p className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded">
                    {userEmail}
                  </p>
                  <p className="text-xs text-gray-500">
                    Check your inbox and spam folder. The code expires in 10 minutes.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={handleCodeChange}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-xl font-mono tracking-widest"
                maxLength={6}
                autoFocus
              />
            </div>

            {method === 'email' && (
              <div className="text-center">
                <button
                  onClick={handleResendCode}
                  disabled={loading || resendCooldown > 0}
                  className="text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  {resendCooldown > 0 
                    ? `Resend code in ${resendCooldown}s` 
                    : 'Resend code'
                  }
                </button>
              </div>
            )}



            <div className={`border rounded-lg p-4 ${
              method === 'totp' 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-start">
                <AlertTriangle className={`w-5 h-5 mr-2 mt-0.5 ${
                  method === 'totp' ? 'text-blue-600' : 'text-amber-600'
                }`} />
                <div>
                  <p className={`text-sm ${
                    method === 'totp' ? 'text-blue-800' : 'text-amber-800'
                  }`}>
                    <strong>Security Tip:</strong> {method === 'totp' 
                      ? 'Never share your authenticator codes with anyone. FlexCOA staff will never ask for your MFA codes.'
                      : 'Never share your email verification codes with anyone. FlexCOA staff will never ask for your codes via phone or chat.'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleVerification}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify'
                )}
              </button>
            </div>

            {/* MFA Recovery Link */}
            <MFARecoveryLink className="mt-4" />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MFAVerification;
